import { useMemo, useState } from "react";
import { AlertTriangle, CheckSquare2, FileSpreadsheet, FileUp, Square } from "lucide-react";
import { toast } from "sonner";
import { importSelectedShopeeProducts, parseShopeeProductFile, type ShopeeProductFile } from "../services/productImports";
import { Modal } from "./Modal";
import { StatusPill } from "./Layout";

export function ProductImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [parsed, setParsed] = useState<ShopeeProductFile>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const selectedProducts = useMemo(() => parsed?.products.filter((product) => selected.has(product.sku)) ?? [], [parsed, selected]);
  const totals = useMemo(() => ({
    listings: selectedProducts.reduce((sum, product) => sum + product.listings.length, 0),
    colors: selectedProducts.reduce((sum, product) => sum + product.colors.length, 0),
    rows: selectedProducts.reduce((sum, product) => sum + product.listings.reduce((count, listing) => count + listing.variants.length, 0), 0),
  }), [selectedProducts]);

  const reset = () => { setParsed(undefined); setSelected(new Set()); };
  const close = () => { if (!busy) { reset(); onClose(); } };
  const chooseFile = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      const result = await parseShopeeProductFile(file);
      setParsed(result);
      setSelected(new Set(result.products.map((product) => product.sku)));
      if (!result.products.length) toast.info("Todos os produtos deste arquivo já foram importados.");
      else if (result.alreadyImportedCount) toast.success(`${result.products.length} produto(s) ainda não importado(s). Os ${result.alreadyImportedCount} já cadastrados foram ocultados.`);
      else toast.success(`${result.products.length} produtos encontrados. Desmarque os que não deseja importar.`);
    } catch (error) {
      reset();
      toast.error(error instanceof Error ? error.message : "Não foi possível ler a planilha da Shopee.");
    } finally { setBusy(false); }
  };
  const toggle = (sku: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(sku)) next.delete(sku); else next.add(sku);
    return next;
  });
  const confirm = async () => {
    if (!parsed) return;
    setBusy(true);
    try {
      const batch = await importSelectedShopeeProducts(parsed, selected);
      toast.success(`${selected.size} produto(s) importado(s). O arquivo agora possui ${batch.productsCount} produto(s) concluído(s).`);
      close();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível importar os produtos.");
    } finally { setBusy(false); }
  };

  return <Modal open={open} onClose={close} title="Importar produtos da Shopee" description="Escolha a planilha, revise e marque somente os SKUs que deseja cadastrar." wide>
    <div className="form-stack product-import-modal">
      {!parsed ? <label className="upload-zone">
        <input type="file" accept=".xls,.xlsx,.csv" onChange={(event) => chooseFile(event.target.files?.[0])} />
        <FileUp size={34} />
        <strong>{busy ? "Lendo anúncios…" : "Selecionar planilha de anúncios"}</strong>
        <span>Exportação da Shopee em XLS, XLSX ou CSV</span>
      </label> : <>
        <div className="file-banner"><FileSpreadsheet size={26} /><div><strong>{parsed.fileName}</strong><span>{parsed.rowCount} linhas · {parsed.totalProductsInFile} SKUs no arquivo · {parsed.products.length} disponíveis</span></div></div>
        {parsed.alreadyImportedCount > 0 && <div className="import-resume-note"><CheckSquare2 size={18} /><div><strong>Importação retomada sem duplicar</strong><span>{parsed.alreadyImportedCount} produto(s) já importado(s) foram ocultados. A lista mostra somente os que ainda podem ser cadastrados.</span></div></div>}
        {parsed.products.length > 0 ? <><div className="product-import-toolbar">
          <div><strong>{selected.size} de {parsed.products.length} produtos selecionados</strong><span>{totals.listings} anúncios · {totals.colors} cores · {totals.rows} linhas</span></div>
          <div className="inline-actions compact-actions">
            <button type="button" className="ghost-button" onClick={() => setSelected(new Set())}><Square size={16} />Limpar</button>
            <button type="button" className="secondary-button" onClick={() => setSelected(new Set(parsed.products.map((product) => product.sku)))}><CheckSquare2 size={16} />Selecionar todos</button>
          </div>
        </div>
        {parsed.warnings.length > 0 && <div className="import-warning"><AlertTriangle size={18} /><span>{parsed.warnings.length} linha(s) incompleta(s) serão ignoradas.</span></div>}
        <div className="product-import-list">
          {parsed.products.map((product) => <label className={`product-import-choice ${selected.has(product.sku) ? "selected" : ""}`} key={product.sku}>
            <input type="checkbox" checked={selected.has(product.sku)} onChange={() => toggle(product.sku)} />
            {product.coverImageUrl ? <img src={product.coverImageUrl} alt="" /> : <span className="product-import-monogram">{product.sku.slice(0, 2)}</span>}
            <div className="product-import-copy">
              <div><code>{product.sku}</code><StatusPill tone={product.action === "novo" ? "success" : "info"}>{product.action === "novo" ? "Novo" : "Atualizar"}</StatusPill></div>
              <strong>{product.name}</strong>
              <span>{product.listings.length} anúncio(s) · {product.colors.length} cores</span>
              <small>{product.colors.join(" · ")}</small>
              {product.warnings.map((warning) => <em key={warning}><AlertTriangle size={13} />{warning}</em>)}
            </div>
          </label>)}
        </div>
        <div className="import-stock-note"><strong>Estoque físico protegido</strong><span>A quantidade anunciada na Shopee será guardada nos dados do anúncio, mas não substituirá o estoque real do Galpão. Novas cores começam com saldo zero.</span></div>
        </> : <div className="product-import-complete"><CheckSquare2 size={34} /><strong>Arquivo já concluído</strong><span>Todos os produtos desta planilha já estão cadastrados. Nenhum item duplicado será criado.</span></div>}
      </>}
      <div className="modal-actions">
        {parsed && <button type="button" className="ghost-button" disabled={busy} onClick={reset}>Trocar arquivo</button>}
        <button type="button" className="ghost-button" disabled={busy} onClick={close}>Cancelar</button>
        {parsed && <button type="button" className="primary-button" disabled={busy || selected.size === 0} onClick={confirm}>{busy ? "Importando…" : `Importar ${selected.size} produto(s)`}</button>}
      </div>
    </div>
  </Modal>;
}
